import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip
} from '@mui/material';
import { DateRange } from 'react-date-range';
import { format, differenceInDays } from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

const DateRangePickerModal = ({ open, onClose, startDate, endDate, onApply }) => {
  const [dateRange, setDateRange] = useState([
    {
      startDate: startDate || new Date(),
      endDate: endDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      key: 'selection'
    }
  ]);

  useEffect(() => {
    // Update local state when props change
    setDateRange([
      {
        startDate: startDate || new Date(),
        endDate: endDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        key: 'selection'
      }
    ]);
  }, [startDate, endDate, open]);

  const handleSelect = (ranges) => {
    setDateRange([ranges.selection]);
  };

  const handleApply = () => {
    onApply(dateRange[0].startDate, dateRange[0].endDate);
    onClose();
  };

  const days = differenceInDays(dateRange[0].endDate, dateRange[0].startDate) + 1;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: 'rgba(30, 41, 59, 0.95)',
          backdropFilter: 'blur(10px)',
          border: '1px solid',
          borderColor: 'primary.dark'
        }
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(99, 102, 241, 0.2)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Select Campaign Duration</Typography>
          <Chip 
            label={`${days} day${days !== 1 ? 's' : ''}`}
            color="primary"
            size="small"
          />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          gap: 2
        }}>
          {/* Selected Date Range Display */}
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            alignItems: 'center',
            p: 2,
            bgcolor: 'rgba(99, 102, 241, 0.1)',
            borderRadius: 2,
            width: '100%',
            justifyContent: 'center'
          }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {format(dateRange[0].startDate, 'MMM dd, yyyy')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              to
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {format(dateRange[0].endDate, 'MMM dd, yyyy')}
            </Typography>
          </Box>

          {/* Date Range Calendar */}
          <Box sx={{ 
            '& .rdrCalendarWrapper': {
              bgcolor: 'transparent',
              color: 'text.primary'
            },
            '& .rdrDateRangePickerWrapper': {
              bgcolor: 'transparent'
            },
            '& .rdrDefinedRangesWrapper': {
              display: 'none' // Hide the preset ranges
            },
            '& .rdrMonth': {
              width: '100%'
            },
            '& .rdrMonthAndYearPickers select': {
              bgcolor: 'rgba(30, 41, 59, 0.8)',
              color: 'text.primary',
              border: '1px solid rgba(99, 102, 241, 0.3)',
              borderRadius: 1,
              padding: '4px 8px',
              '&:hover': {
                borderColor: 'primary.main'
              }
            },
            '& .rdrMonthAndYearPickers': {
              color: 'text.primary'
            },
            '& .rdrDayToday .rdrDayNumber span': {
              '&:after': {
                bgcolor: 'primary.main'
              }
            },
            '& .rdrDay': {
              color: '#ffffff !important'
            },
            '& .rdrDayNumber': {
              position: 'relative',
              zIndex: 2
            },
            '& .rdrDayNumber span': {
              color: '#ffffff !important'
            },
            '& .rdrDayDisabled': {
              bgcolor: 'transparent !important'
            },
            '& .rdrDayDisabled .rdrDayNumber span': {
              color: '#4a5568 !important',
              opacity: 0.5
            },
            '& .rdrDayPassive': {
              bgcolor: 'transparent !important'
            },
            '& .rdrDayPassive .rdrDayNumber span': {
              color: '#4a5568 !important',
              opacity: 0.3
            },
            '& .rdrDayHovered': {
              bgcolor: 'rgba(99, 102, 241, 0.2) !important'
            },
            '& .rdrDayStartPreview, & .rdrDayEndPreview, & .rdrDayInPreview': {
              bgcolor: 'rgba(99, 102, 241, 0.15)',
              borderColor: 'transparent'
            },
            '& .rdrDayStartOfMonth .rdrDayInPreview, & .rdrDayStartOfWeek .rdrDayInPreview': {
              bgcolor: 'rgba(99, 102, 241, 0.15)',
              borderColor: 'transparent'
            },
            '& .rdrInRange': {
              bgcolor: 'rgba(99, 102, 241, 0.2) !important',
              color: '#ffffff !important'
            },
            '& .rdrStartEdge, & .rdrEndEdge': {
              bgcolor: '#6366F1 !important'
            },
            '& .rdrStartEdge .rdrDayNumber span, & .rdrEndEdge .rdrDayNumber span': {
              color: '#ffffff !important',
              fontWeight: 600
            },
            '& .rdrDayStartPreview, & .rdrDayEndPreview': {
              bgcolor: 'rgba(99, 102, 241, 0.5) !important',
              border: '2px solid #6366F1 !important'
            },
            '& .rdrDayStartPreview .rdrDayNumber span, & .rdrDayEndPreview .rdrDayNumber span': {
              color: '#ffffff !important',
              fontWeight: 600
            },
            '& .rdrWeekDay': {
              color: '#9ca3af',
              fontWeight: 600
            },
            '& .rdrMonthName': {
              color: '#ffffff',
              fontWeight: 600
            },
            '& .rdrNextPrevButton': {
              bgcolor: 'rgba(99, 102, 241, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(99, 102, 241, 0.2)'
              }
            },
            '& .rdrPprevButton i': {
              borderColor: 'transparent rgb(99, 102, 241) transparent transparent'
            },
            '& .rdrNextButton i': {
              borderColor: 'transparent transparent transparent rgb(99, 102, 241)'
            },
            // Fix for overlapping styles
            '& .rdrDayToday': {
              '& .rdrDayNumber span': {
                '&:after': {
                  background: '#6366F1',
                  bottom: '2px',
                  content: '""',
                  height: '2px',
                  left: '50%',
                  position: 'absolute',
                  transform: 'translateX(-50%)',
                  width: '80%'
                }
              }
            }
          }}>
            <DateRange
              ranges={dateRange}
              onChange={handleSelect}
              moveRangeOnFirstSelection={false}
              months={1}
              direction="horizontal"
              rangeColors={['#6366F1']}
              showDateDisplay={false}
              minDate={new Date()}
            />
          </Box>

          {/* Quick Select Options */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const today = new Date();
                const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
                setDateRange([{ startDate: today, endDate: weekFromNow, key: 'selection' }]);
              }}
              sx={{ borderColor: 'primary.dark', color: 'primary.light' }}
            >
              1 Week
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const today = new Date();
                const twoWeeksFromNow = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000);
                setDateRange([{ startDate: today, endDate: twoWeeksFromNow, key: 'selection' }]);
              }}
              sx={{ borderColor: 'primary.dark', color: 'primary.light' }}
            >
              2 Weeks
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const today = new Date();
                const monthFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
                setDateRange([{ startDate: today, endDate: monthFromNow, key: 'selection' }]);
              }}
              sx={{ borderColor: 'primary.dark', color: 'primary.light' }}
            >
              30 Days
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const today = new Date();
                const twoMonthsFromNow = new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000);
                setDateRange([{ startDate: today, endDate: twoMonthsFromNow, key: 'selection' }]);
              }}
              sx={{ borderColor: 'primary.dark', color: 'primary.light' }}
            >
              60 Days
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const today = new Date();
                const quarterFromNow = new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000);
                setDateRange([{ startDate: today, endDate: quarterFromNow, key: 'selection' }]);
              }}
              sx={{ borderColor: 'primary.dark', color: 'primary.light' }}
            >
              Quarter
            </Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(99, 102, 241, 0.2)' }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleApply}
          variant="contained"
          sx={{
            background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
            '&:hover': {
              background: 'linear-gradient(135deg, #818CF8, #A78BFA)',
            }
          }}
        >
          Apply Date Range
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DateRangePickerModal;